import React from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useCheckbox } from './useCheckbox';
import { Group } from './Group';

const InputWrapper = styled.span`
  display: inline-block;
`;

const Indicator = styled.span`
  box-sizing: border-box;
  position: relative;
  display: block;
  width: ${themeGet('checkbox.indicator.size')};
  height: ${themeGet('checkbox.indicator.size')};
  border: ${themeGet('checkbox.indicator.border')};
  border-radius: ${themeGet('checkbox.indicator.borderRadius')};
  border-color: ${themeGet('checkbox.indicator.borderColor')};
  background-color: ${themeGet('checkbox.indicator.backgroundColor')};
  transition: background-color 180ms;

  ${props =>
    props.checked &&
    css`
      border-color: ${themeGet('checkbox.indicator.checked.borderColor')};
      background-color: ${themeGet('checkbox.indicator.checked.backgroundColor')};
    `}

  ${props =>
    props.disabled &&
    css`
      border-color: ${themeGet('checkbox.indicator.disabled.borderColor')};
      background-color: ${themeGet('checkbox.indicator.disabled.backgroundColor')};
    `}
`;

const Input = styled.input`
  display: none;
`;

const Wrapper = styled.label`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex: 1;
  width: 100%;
  cursor: pointer;

  &:hover ${Indicator} {
    border-color: ${themeGet('checkbox.indicator.hover.borderColor')};
  }

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;

      &:hover ${Indicator} {
        border-color: ${themeGet('checkbox.indicator.disabled.borderColor')};
      }
    `}
`;

export const Text = styled.span`
  padding: 0 8px;
  font-size: ${themeGet('checkbox.text.fontSize')};
  line-height: 1.5;
  user-select: none;
  color: ${themeGet('checkbox.text.color')};

  ${props =>
    props.disabled &&
    css`
      color: ${themeGet('checkbox.text.disabled.color')};
    `}
`;

// we should change Square and Text to svg, if we want to animate them.
const Square = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 50%;
  height: 50%;
  border: 0;
  border-radius: 1px;
  transform: translate(-50%, -50%) scale(1);
  background-color: ${themeGet('checkbox.indicator.indeterminate.backgroundColor')};

  ${props =>
    props.disabled &&
    css`
      background-color: ${themeGet('checkbox.indicator.indeterminate.disabled.backgroundColor')};
    `}
`;

const Tick = styled.span`
  position: absolute;
  left: 50%;
  top: 10%;
  width: 25%;
  height: 50%;
  border: 2px solid;
  border-color: ${themeGet('checkbox.indicator.icon.borderColor')};
  border-top: 0;
  border-left: 0;
  transform: translateX(-50%) rotate(45deg);
`;

export const Checkbox = ({ indeterminate, disabled, children, checked, name, value, onChange, ...rest }) => {
  const { isChecked, handleChange } = useCheckbox({ checked, onChange });

  return (
    <Wrapper disabled={disabled}>
      <InputWrapper>
        <Input
          type="checkbox"
          name={name}
          value={value}
          checked={isChecked}
          disabled={disabled}
          onChange={handleChange}
          {...rest}
        />
        <Indicator checked={isChecked} disabled={disabled}>
          {isChecked ? <Tick /> : indeterminate ? <Square disabled={disabled} /> : null}
        </Indicator>
      </InputWrapper>
      {children && <Text disabled={disabled}>{children}</Text>}
    </Wrapper>
  );
};

Checkbox.Group = Group;
